import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
  Collapse,
  Divider,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import {setMessageFeedback, setTextDefinitionMode, triggerClearTextInput} from './chatSlice';
import SaveAsReportModal from './SaveAsReportModal';
import {fetchPostAuthSafe} from '../../app/utils';
import {
  captureElementToCanvas,
  captureIframeDocumentToCanvas,
  mergeCanvasesVertical,
  downloadPngFromCanvas,
  downloadPdfFromCanvases,
} from './messageExport';
import {downloadFile} from '../../app/fileDownload';

const SANDBOX = 'allow-same-origin allow-scripts allow-forms allow-popups allow-modals';

/**
 * Collapsible analysis transparency section — renders server-authored prose only.
 * @param {Object} props - Component props
 * @param {Object} [props.analysisContext] - Server-derived analysis disclosure payload
 * @return {JSX.Element|null}
 */
function AnalysisDetailsSection({analysisContext}) {
  const [open, setOpen] = useState(false);

  if (!analysisContext?.summary || !Array.isArray(analysisContext.sections) || analysisContext.sections.length === 0) {
    return null;
  }

  return (
    <Box sx={{mt: 1, px: 0}}>
      <Box
        onClick={() => {
          setOpen((prev) => {
            return !prev;
          });
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {opacity: 0.85},
        }}
      >
        <Typography variant='caption' sx={{color: '#666', fontWeight: 500}}>
          {open ? '▼' : '▶'} Analysis details
        </Typography>
        {!open && (
          <Typography variant='caption' sx={{color: '#888', fontStyle: 'italic'}}>
            — {analysisContext.summary}
          </Typography>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{pt: 0.75, pl: 1.5, borderLeft: '2px solid #e0e0e0', ml: 0.5}}>
          {analysisContext.sections.map((section) => {
            return (
              <Box key={section.heading} sx={{mb: 1}}>
                <Typography variant='caption' sx={{color: '#555', fontWeight: 600, display: 'block', mb: 0.25}}>
                  {section.heading}
                </Typography>
                {(section.body || []).map((line, i) => {
                  return (
                    <Typography
                      key={`${section.heading}-${i}`}
                      variant='caption'
                      component='p'
                      sx={{color: '#666', m: 0, mb: 0.35, lineHeight: 1.45}}
                    >
                      {line}
                    </Typography>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

const LAYOUT_CONFIG = {
  fullPage: {
    textMaxWidth: '70%',
    htmlMaxWidth: '90%',
    iframeStyle: {
      width: '100%',
      height: '680px',
      minHeight: '400px',
      border: 'none',
      background: 'white',
      display: 'block',
    },
  },
  panel: {
    textMaxWidth: '80%',
    htmlMaxWidth: '90%',
    iframeStyle: {
      width: '100%',
      height: '360px',
      minHeight: '200px',
      border: 'none',
      background: 'white',
      display: 'block',
    },
  },
};

/**
 * One Insights chat message (Markdown and/or HTML iframe) with assistant export actions.
 *
 * @param {Object} props
 * @param {Object} props.msg - role, content, html?, timestamp?, createdAt?, statusUpdate?
 * @param {number} props.index - iframe title suffix
 * @param {'fullPage'|'panel'} [props.layout='fullPage']
 * @return {JSX.Element}
 */
/**
 * Multi-select checkboxes for entity selection (task names, vehicles, etc.).
 * @param {Object} props
 * @param {Array} props.options - Array of option objects or strings.
 * @param {Function} props.onSubmit - Callback with selected labels array.
 * @return {JSX.Element}
 */
function EntitySelectOptions({options, onSubmit}) {
  const [selected, setSelected] = React.useState([]);
  const [submitted, setSubmitted] = React.useState(false);

  const {suggested, others} = React.useMemo(() => {
    const sugg = [];
    const rest = [];
    for (const opt of options) {
      const label = typeof opt === 'string' ? opt : opt.label;
      const isSuggested = typeof opt === 'object' && opt.suggested;
      (isSuggested ? sugg : rest).push(label);
    }
    const sort = (a, b) => {
      return a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'});
    };
    return {suggested: sugg.sort(sort), others: rest.sort(sort)};
  }, [options]);

  const [showAll, setShowAll] = React.useState(false);

  const handleToggle = (label) => {
    setSelected((prev) => {
      return prev.includes(label)
        ? prev.filter((l) => {
            return l !== label;
          })
        : [...prev, label];
    });
  };

  const renderCheckbox = (label) => {
    const isChecked = selected.includes(label);
    return (
      <Box
        key={label}
        onClick={() => {
          return handleToggle(label);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          mb: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: isChecked ? 'rgba(5, 105, 6, 0.08)' : 'transparent',
          border: '1px solid',
          borderColor: isChecked ? '#056906' : '#e0e0e0',
          '&:hover': {bgcolor: 'rgba(0,0,0,0.04)'},
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '3px',
            border: '2px solid',
            borderColor: isChecked ? '#056906' : '#9e9e9e',
            bgcolor: isChecked ? '#056906' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isChecked && (
            <Typography sx={{color: '#fff', fontSize: 12, lineHeight: 1, fontWeight: 'bold'}}>✓</Typography>
          )}
        </Box>
        <Typography variant='body2' sx={{color: '#333'}}>
          {label}
        </Typography>
      </Box>
    );
  };

  const gridSx = (items) => {
    return items.length > 6
      ? {
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0.5,
        }
      : {};
  };

  return (
    <Box sx={{mt: 2}}>
      {suggested.length > 0 && (
        <>
          <Typography
            variant='caption'
            sx={{color: '#056906', fontWeight: 600, textTransform: 'uppercase', mb: 0.5, display: 'block'}}
          >
            Suggested
          </Typography>
          <Box sx={gridSx(suggested)}>{suggested.map(renderCheckbox)}</Box>
        </>
      )}
      {others.length > 0 && (
        <>
          {suggested.length > 0 && (
            <Button
              size='small'
              onClick={() => {
                setShowAll(!showAll);
              }}
              sx={{mt: 1, mb: 0.5, textTransform: 'none', color: '#666', fontSize: '0.75rem'}}
            >
              {showAll ? 'Hide others' : `Show all others (${others.length})`}
            </Button>
          )}
          {(suggested.length === 0 || showAll) && <Box sx={gridSx(others)}>{others.map(renderCheckbox)}</Box>}
        </>
      )}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5}}>
        <Button
          variant='contained'
          size='small'
          disabled={selected.length === 0 || submitted}
          onClick={() => {
            setSubmitted(true);
            return onSubmit(selected);
          }}
          sx={{
            bgcolor: '#056906',
            '&:hover': {bgcolor: '#044d05'},
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {submitted ? 'Submitted' : `Submit Selection (${selected.length})`}
        </Button>
        {selected.length > 0 && !submitted && (
          <Button
            size='small'
            onClick={() => {
              setSelected([]);
            }}
            sx={{textTransform: 'none', color: '#999', fontSize: '0.75rem'}}
          >
            Clear selection
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * Unified multi-select group builder supporting two modes:
 * - 'definition': full group builder with text fallback, indeterminate checkboxes,
 *   collapsible individual vehicles, subGroups for locations. Empty initial selection.
 * - 'entitySelect': pre-selects suggested items, no text fallback, flat model groups.
 *
 * Data shape: options = [{groupLabel, groupType, defaultOpen, modelItems?, subGroups?, items}]
 * - Vehicle sections may have modelItems (definition) or groupType='vehicleNameGroup' (entitySelect).
 * - Location sections may have subGroups [{groupLabel, groupType, items}].
 * Submits array of submitLabel (or label) values.
 * @param {Object} props
 * @param {Array} props.options - Array of group/section objects.
 * @param {Function} props.onSubmit - Callback with selected submitLabel array.
 * @param {string} [props.mode='definition'] - 'definition' or 'entitySelect'.
 * @return {JSX.Element}
 */
function GroupSelectOptions({options, onSubmit, mode = 'definition'}) {
  const textDefinitionMode = useSelector((state) => {
    return state.chat.textDefinitionMode;
  });
  const isDefinition = mode === 'definition';
  const normalize = (s) => {
    return String(s || '')
      .toLowerCase()
      .replace(/([a-z])(\d)/g, '$1 $2')
      .replace(/(\d)([a-z])/g, '$1 $2')
      .replace(/[^\w]+/g, ' ')
      .trim();
  };

  const [selected, setSelected] = React.useState(() => {
    // Pre-select only exact matches, not fuzzy suggestions
    const init = new Set();
    for (const group of options) {
      for (const item of group.items || []) {
        if (item.exactMatch) init.add(item.submitLabel || item.label);
      }
      for (const model of group.modelItems || []) {
        if (model.exactMatch) init.add(model.submitLabel || model.label);
      }
      for (const sub of group.subGroups || []) {
        for (const item of sub.items || []) {
          if (item.exactMatch) init.add(item.submitLabel || item.label);
        }
        for (const model of sub.modelItems || []) {
          if (model.exactMatch) init.add(model.submitLabel || model.label);
        }
      }
    }
    return init;
  });
  const [submitted, setSubmitted] = React.useState(false);
  const [typedText, setTypedText] = React.useState('');
  const [openSections, setOpenSections] = React.useState(() => {
    const init = {};
    for (const s of options) {
      init[s.groupLabel] = s.defaultOpen ?? false;
      if (s.subGroups) {
        for (const sub of s.subGroups) init[sub.groupLabel] = false;
      }
    }
    return init;
  });
  const [showAllVehicles, setShowAllVehicles] = React.useState(false);
  const [showLabelGroups, setShowLabelGroups] = React.useState(false);
  const [showNameGroups, setShowNameGroups] = React.useState(false);

  const gdDispatch = useDispatch();

  const vehicleSection = options.find((s) => {
    return s.groupType === 'vehicle';
  });
  const getSubmitLabel = (item) => {
    return item.submitLabel || item.label;
  };
  const vehicleModelSubmitLabels = React.useMemo(() => {
    const labels = new Set();
    for (const item of vehicleSection?.modelItems || []) {
      labels.add(getSubmitLabel(item));
    }
    return labels;
  }, [vehicleSection]);
  const isVehicleModelItem = (item) => {
    const submitLabel = getSubmitLabel(item);
    return item.groupType === 'vehicleNameGroup' || vehicleModelSubmitLabels.has(submitLabel);
  };

  // Build lookup from submitLabel → structured object for submission
  const itemLookup = React.useMemo(() => {
    const map = {};
    for (const section of options) {
      const sectionType = section.groupType;
      for (const item of section.items || []) {
        map[getSubmitLabel(item)] = {type: sectionType, label: item.label, submitLabel: getSubmitLabel(item)};
      }
      for (const model of section.modelItems || []) {
        map[getSubmitLabel(model)] = {
          type: model.groupType || 'vehicleNameGroup',
          label: model.label,
          submitLabel: getSubmitLabel(model),
        };
      }
      for (const sub of section.subGroups || []) {
        for (const model of sub.modelItems || []) {
          map[getSubmitLabel(model)] = {
            type: model.groupType || sub.groupType,
            label: model.label,
            submitLabel: getSubmitLabel(model),
          };
        }
        for (const item of sub.items || []) {
          map[getSubmitLabel(item)] = {type: sub.groupType, label: item.label, submitLabel: getSubmitLabel(item)};
        }
      }
    }
    return map;
  }, [options]);

  const toggleItem = (item) => {
    const key = getSubmitLabel(item);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const modelMatchKeysBySubmitLabel = React.useMemo(() => {
    const map = {};
    const vehicleItems = vehicleSection?.items || [];
    const vehicleModels = vehicleSection?.modelItems || [];
    const normalizedVehicleItems = vehicleItems.map((item) => {
      return {
        submitLabel: getSubmitLabel(item),
        normalizedLabel: normalize(item.label),
      };
    });

    for (const modelItem of vehicleModels) {
      const modelSubmitLabel = getSubmitLabel(modelItem);
      const pattern = normalize(modelItem.label);
      map[modelSubmitLabel] = normalizedVehicleItems
        .filter((vehicleItem) => {
          return vehicleItem.normalizedLabel.includes(pattern);
        })
        .map((vehicleItem) => {
          return vehicleItem.submitLabel;
        });
    }

    return map;
  }, [vehicleSection]);
  const getModelMatchKeys = (modelItem) => {
    return modelMatchKeysBySubmitLabel[getSubmitLabel(modelItem)] || [];
  };

  const toggleModelGroup = (modelItem) => {
    const keys = getModelMatchKeys(modelItem);
    if (keys.length === 0) {
      // Individual items not available (tooMany) — toggle the model group itself
      toggleItem(modelItem);
      return;
    }
    const allChecked = keys.every((k) => {
      return selected.has(k);
    });
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => {
        allChecked ? next.delete(k) : next.add(k);
      });
      return next;
    });
    // Expand the individual vehicles list so the user can see which items were toggled
    if (vehicleSection) {
      setOpenSections((prev) => {
        return {...prev, [vehicleSection.groupLabel]: true};
      });
      if (isDefinition) setShowAllVehicles(true);
    }
  };

  const isModelGroupChecked = (modelItem) => {
    const keys = getModelMatchKeys(modelItem);
    if (keys.length === 0) return selected.has(getSubmitLabel(modelItem));
    return keys.every((k) => {
      return selected.has(k);
    });
  };

  const isModelGroupIndeterminate = (modelItem) => {
    if (!isDefinition) return false;
    const keys = getModelMatchKeys(modelItem);
    if (keys.length === 0) return false;
    const n = keys.filter((k) => {
      return selected.has(k);
    }).length;
    return n > 0 && n < keys.length;
  };

  const renderCheckbox = (item, onToggle, checked, indeterminate) => {
    const isSuggested = item.suggested && !checked;
    return (
      <Box
        key={getSubmitLabel(item)}
        onClick={submitted ? undefined : onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          mb: 0.5,
          borderRadius: 1,
          cursor: submitted ? 'default' : 'pointer',
          opacity: submitted && !checked && !indeterminate ? 0.4 : 1,
          bgcolor:
            checked || indeterminate ? 'rgba(5, 105, 6, 0.08)' : isSuggested ? 'rgba(5, 105, 6, 0.04)' : 'transparent',
          border: '1px solid',
          borderColor: checked || indeterminate ? '#056906' : isSuggested ? 'rgba(5, 105, 6, 0.3)' : '#e0e0e0',
          '&:hover': submitted ? {} : {bgcolor: 'rgba(0,0,0,0.04)'},
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '3px',
            border: '2px solid',
            flexShrink: 0,
            borderColor: checked || indeterminate ? '#056906' : '#9e9e9e',
            bgcolor: checked ? '#056906' : indeterminate ? 'rgba(5,105,6,0.4)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {(checked || indeterminate) && (
            <Typography sx={{color: '#fff', fontSize: 12, lineHeight: 1, fontWeight: 'bold'}}>
              {indeterminate ? '–' : '✓'}
            </Typography>
          )}
        </Box>
        <Typography variant='body2' sx={{color: '#333', flex: 1}}>
          {item.label}
        </Typography>
        {item.count != null && (
          <Typography variant='caption' sx={{color: '#999'}}>
            {isDefinition ? `${item.count} ${item.groupType === 'areaType' ? 'areas' : 'vehicles'}` : item.count}
          </Typography>
        )}
      </Box>
    );
  };

  const gridSx = (count) => {
    return count > 6 ? {display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5} : {};
  };

  const renderItemCheckbox = (item) => {
    const isModel = isVehicleModelItem(item);
    return renderCheckbox(
      item,
      () => {
        isModel ? toggleModelGroup(item) : toggleItem(item);
      },
      isModel ? isModelGroupChecked(item) : selected.has(getSubmitLabel(item)),
      isModel ? isModelGroupIndeterminate(item) : false
    );
  };

  // Render a flat group of items — exact + fuzzy matches grouped under "Suggested",
  // everything else below. Exact-match items also appear in the top-level "Pre-selected"
  // strip; selection state stays in sync via the shared `selected` Set.
  const renderFlatItems = (items) => {
    const fuzzy = items.filter((i) => {
      return i.suggested || i.exactMatch;
    });
    const rest = items.filter((i) => {
      return !i.suggested && !i.exactMatch;
    });

    return (
      <Box sx={{pt: 0.5}}>
        {fuzzy.length > 0 && (
          <>
            <Typography variant='caption' sx={{color: '#056906', fontWeight: 600, px: 1, pb: 0.5, display: 'block'}}>
              Suggested
            </Typography>
            <Box sx={gridSx(fuzzy.length)}>{fuzzy.map(renderItemCheckbox)}</Box>
          </>
        )}
        {rest.length > 0 && (
          <Box
            sx={{
              ...gridSx(rest.length),
              ...(fuzzy.length > 0 ? {mt: 0.5, pt: 0.5, borderTop: '1px solid #e0e0e0'} : {}),
            }}
          >
            {rest.map(renderItemCheckbox)}
          </Box>
        )}
      </Box>
    );
  };

  // Render vehicle section with modelItems + collapsible individual list (definition mode)
  const renderVehicleSection = (section) => {
    const allModels = section.modelItems || [];
    const allItems = section.items || [];
    const LABEL_GROUP_TYPES = new Set(['vehicleLabel', 'implementLabel']);
    const fuzzyModels = allModels.filter((m) => {
      return m.suggested || m.exactMatch;
    });
    const labelModels = allModels.filter((m) => {
      return !m.suggested && !m.exactMatch && LABEL_GROUP_TYPES.has(m.groupType);
    });
    const otherModels = allModels.filter((m) => {
      return !m.suggested && !m.exactMatch && !LABEL_GROUP_TYPES.has(m.groupType);
    });
    const fuzzyItems = allItems.filter((i) => {
      return i.suggested || i.exactMatch;
    });
    const otherItems = allItems.filter((i) => {
      return !i.suggested && !i.exactMatch;
    });
    const hasSuggested = fuzzyModels.length + fuzzyItems.length > 0;

    return (
      <Box sx={{pt: 0.5}}>
        {hasSuggested && (
          <>
            <Typography variant='caption' sx={{color: '#056906', fontWeight: 600, px: 1, pb: 0.5, display: 'block'}}>
              Suggested
            </Typography>
            <Box sx={gridSx(fuzzyModels.length + fuzzyItems.length)}>
              {fuzzyModels.map(renderItemCheckbox)}
              {fuzzyItems.map(renderItemCheckbox)}
            </Box>
          </>
        )}
        {labelModels.length > 0 && (
          <>
            <Box
              onClick={() => {
                setShowLabelGroups((p) => {
                  return !p;
                });
              }}
              sx={{
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                ...(hasSuggested ? {borderTop: '1px solid #e0e0e0', mt: 0.5} : {}),
              }}
            >
              <Typography variant='caption' sx={{color: '#056906'}}>
                {showLabelGroups ? '▼' : '▶'}{' '}
                {section.groupType === 'implement' ? 'Implement Labels' : 'Vehicle Labels'} ({labelModels.length})
              </Typography>
            </Box>
            <Collapse in={showLabelGroups} unmountOnExit>
              <Box sx={gridSx(labelModels.length)}>{labelModels.map(renderItemCheckbox)}</Box>
            </Collapse>
          </>
        )}
        {otherModels.length > 0 && (
          <>
            <Box
              onClick={() => {
                setShowNameGroups((p) => {
                  return !p;
                });
              }}
              sx={{
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                ...(hasSuggested || labelModels.length > 0 ? {borderTop: '1px solid #e0e0e0', mt: 0.5} : {}),
              }}
            >
              <Typography variant='caption' sx={{color: '#056906'}}>
                {showNameGroups ? '▼' : '▶'}{' '}
                {section.groupType === 'implement'
                  ? 'Implements with names that contain...'
                  : 'Vehicles with names that contain...'}{' '}
                ({otherModels.length})
              </Typography>
            </Box>
            <Collapse in={showNameGroups} unmountOnExit>
              <Box sx={gridSx(otherModels.length)}>{otherModels.map(renderItemCheckbox)}</Box>
            </Collapse>
          </>
        )}
        {otherItems.length > 0 && (
          <>
            <Box
              onClick={() => {
                setShowAllVehicles((p) => {
                  return !p;
                });
              }}
              sx={{
                cursor: 'pointer',
                mt: 0.5,
                px: 1,
                py: 0.5,
                color: '#777',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant='caption' sx={{color: '#056906'}}>
                {showAllVehicles
                  ? `▼ Individual ${section.groupType === 'implement' ? 'implements' : 'vehicles'} (${
                      otherItems.length
                    })`
                  : `▶ Individual ${section.groupType === 'implement' ? 'implements' : 'vehicles'} (${
                      otherItems.length
                    })`}
              </Typography>
            </Box>
            <Collapse in={showAllVehicles} unmountOnExit>
              {showAllVehicles && (
                <Box sx={gridSx(otherItems.length)}>
                  {otherItems.map((v) => {
                    return renderCheckbox(
                      v,
                      () => {
                        toggleItem(v);
                      },
                      selected.has(getSubmitLabel(v))
                    );
                  })}
                </Box>
              )}
            </Collapse>
          </>
        )}
      </Box>
    );
  };

  // Render section with nested sub-groups (e.g. Locations → Regions, Fields, Blocks)
  const renderSubGroups = (section) => {
    return (
      <Box sx={{pt: 0.5}}>
        {section.subGroups.map((sub) => {
          const isSubOpen = Boolean(openSections[sub.groupLabel]);
          return (
            <Box key={sub.groupLabel} sx={{mb: 1}}>
              <Box
                onClick={() => {
                  setOpenSections((prev) => {
                    return {...prev, [sub.groupLabel]: !prev[sub.groupLabel]};
                  });
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  px: 1.5,
                  py: 0.5,
                  bgcolor: '#fafafa',
                  borderRadius: 1,
                  '&:hover': {bgcolor: '#f0f0f0'},
                }}
              >
                <Typography variant='caption' sx={{color: '#aaa', width: 12, flexShrink: 0}}>
                  {isSubOpen ? '▼' : '▶'}
                </Typography>
                <Typography variant='caption' sx={{fontWeight: 600, color: '#666'}}>
                  {sub.groupLabel}
                </Typography>
              </Box>
              <Collapse in={isSubOpen} unmountOnExit>
                {isSubOpen &&
                  (sub.tooMany
                    ? renderTooManyNotice(sub.tooMany)
                    : (() => {
                        const modelItems = sub.modelItems || [];
                        const fuzzyModels = modelItems.filter((m) => {
                          return m.suggested || m.exactMatch;
                        });
                        const otherModels = modelItems.filter((m) => {
                          return !m.suggested && !m.exactMatch;
                        });
                        const fuzzy = (sub.items || []).filter((i) => {
                          return i.suggested || i.exactMatch;
                        });
                        const rest = (sub.items || []).filter((i) => {
                          return !i.suggested && !i.exactMatch;
                        });
                        const hasSuggested = fuzzyModels.length + fuzzy.length > 0;
                        return (
                          <Box sx={{pl: 1}}>
                            {hasSuggested && (
                              <>
                                <Typography
                                  variant='caption'
                                  sx={{color: '#056906', fontWeight: 600, px: 0.5, pb: 0.5, display: 'block'}}
                                >
                                  Suggested
                                </Typography>
                                <Box sx={gridSx(fuzzyModels.length + fuzzy.length)}>
                                  {fuzzyModels.map(renderItemCheckbox)}
                                  {fuzzy.map(renderItemCheckbox)}
                                </Box>
                              </>
                            )}
                            {otherModels.length > 0 && (
                              <>
                                <Typography
                                  variant='caption'
                                  sx={{
                                    color: '#888',
                                    fontStyle: 'italic',
                                    px: 0.5,
                                    pb: 0.5,
                                    display: 'block',
                                    ...(hasSuggested ? {borderTop: '1px solid #e0e0e0', mt: 0.5, pt: 0.5} : {}),
                                  }}
                                >
                                  By type
                                </Typography>
                                <Box sx={gridSx(otherModels.length)}>{otherModels.map(renderItemCheckbox)}</Box>
                              </>
                            )}
                            {rest.length > 0 && (
                              <Box
                                sx={{
                                  ...(hasSuggested || otherModels.length > 0
                                    ? {mt: 0.5, pt: 0.5, borderTop: '1px solid #e0e0e0'}
                                    : {}),
                                }}
                              >
                                {modelItems.length > 0 && (
                                  <Typography
                                    variant='caption'
                                    sx={{color: '#888', fontStyle: 'italic', px: 0.5, pb: 0.5, display: 'block'}}
                                  >
                                    Individual
                                  </Typography>
                                )}
                                <Box sx={gridSx(rest.length)}>
                                  {rest.map((item) => {
                                    return renderCheckbox(
                                      item,
                                      () => {
                                        toggleItem(item);
                                      },
                                      selected.has(getSubmitLabel(item))
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      })())}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderTooManyNotice = (count) => {
    return (
      <Box sx={{px: 1.5, py: 1, color: '#888', fontStyle: 'italic'}}>
        <Typography variant='caption'>
          Too many individuals to display ({count}). Please type your selection in the chat instead.
        </Typography>
      </Box>
    );
  };

  // Choose render path based on section data shape
  const renderSectionContent = (section) => {
    if (section.tooMany) {
      // For vehicles, still show model groups even when individual list is too long
      if (section.modelItems && isDefinition) {
        return (
          <Box sx={{pt: 0.5}}>
            {renderVehicleSection({...section, items: []})}
            {renderTooManyNotice(section.tooMany)}
          </Box>
        );
      }
      return renderTooManyNotice(section.tooMany);
    }
    if (section.modelItems && isDefinition) return renderVehicleSection(section);
    if (section.subGroups) return renderSubGroups(section);
    return renderFlatItems(section.items || []);
  };

  // Collect exact-match pre-selected items across all sections for top-level display
  const preSelectedItems = React.useMemo(() => {
    const items = [];
    for (const section of options) {
      for (const item of section.modelItems || []) {
        if (item.exactMatch) items.push(item);
      }
      for (const item of section.items || []) {
        if (item.exactMatch) items.push(item);
      }
      for (const sub of section.subGroups || []) {
        for (const model of sub.modelItems || []) {
          if (model.exactMatch) items.push(model);
        }
        for (const item of sub.items || []) {
          if (item.exactMatch) items.push(item);
        }
      }
    }
    return items;
  }, [options]);

  return (
    <Box sx={{mt: 2}}>
      <Box
        onClick={
          textDefinitionMode
            ? () => {
                return gdDispatch(setTextDefinitionMode(false));
              }
            : undefined
        }
        sx={{
          opacity: textDefinitionMode ? 0.45 : 1,
          cursor: textDefinitionMode ? 'pointer' : 'default',
          transition: 'opacity 0.2s ease',
          ...(textDefinitionMode ? {'& *': {pointerEvents: 'none'}} : {}),
        }}
      >
        {preSelectedItems.length > 0 && (
          <Box sx={{mb: 1.5, pb: 1, borderBottom: '1px solid #e0e0e0'}}>
            <Typography
              variant='caption'
              sx={{fontWeight: 700, textTransform: 'uppercase', color: '#056906', px: 1, pb: 0.5, display: 'block'}}
            >
              Pre-selected
            </Typography>
            <Box sx={gridSx(preSelectedItems.length)}>{preSelectedItems.map(renderItemCheckbox)}</Box>
          </Box>
        )}
        {options.map((section) => {
          const isSectionOpen = Boolean(openSections[section.groupLabel]);
          return (
            <Box key={section.groupLabel} sx={{mb: 1.5}}>
              <Box
                onClick={() => {
                  setOpenSections((prev) => {
                    return {...prev, [section.groupLabel]: !prev[section.groupLabel]};
                  });
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  px: 1,
                  py: 0.75,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  '&:hover': {bgcolor: '#eeeeee'},
                }}
              >
                <Typography variant='caption' sx={{color: '#888', width: 12, flexShrink: 0}}>
                  {isSectionOpen ? '▼' : '▶'}
                </Typography>
                <Typography variant='caption' sx={{fontWeight: 700, textTransform: 'uppercase', color: '#555'}}>
                  {section.groupLabel}
                </Typography>
              </Box>
              <Collapse in={isSectionOpen} unmountOnExit>
                {isSectionOpen ? renderSectionContent(section) : null}
              </Collapse>
            </Box>
          );
        })}
        {selected.size > 0 &&
          !submitted &&
          (() => {
            const dimensionLabels = {
              vehicle: 'Vehicles',
              vehicleNameGroup: 'Vehicle Name Groups',
              implement: 'Implements',
              operator: 'Operators',
              task: 'Tasks',
              region: 'Regions',
              field: 'Fields',
              block: 'Blocks',
              area: 'Other Areas',
              regions: 'Regions',
              fields: 'Fields',
              blocks: 'Blocks',
              nonAcAreas: 'Other Areas',
            };
            const byDim = {};
            for (const key of selected) {
              const item = itemLookup[key];
              const dim = item?.type || 'other';
              if (!byDim[dim]) byDim[dim] = [];
              byDim[dim].push(item ? item.label : key);
            }
            return (
              <Box sx={{mt: 1.5, px: 1, py: 1, bgcolor: '#f9fdf9', border: '1px solid #e0e0e0', borderRadius: 1}}>
                <Typography variant='caption' sx={{fontWeight: 600, color: '#333', display: 'block', mb: 0.5}}>
                  Currently selected ({selected.size}):
                </Typography>
                {Object.entries(byDim).map(([dim, labels]) => {
                  return (
                    <Box key={dim} sx={{mb: 0.25}}>
                      <Typography variant='caption' sx={{fontWeight: 600, color: '#666', display: 'block'}}>
                        {dimensionLabels[dim] || dim}:
                      </Typography>
                      <Typography variant='caption' sx={{color: '#555', lineHeight: 1.6, display: 'block', pl: 0.5}}>
                        {labels.join(', ')}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        <Box sx={{mt: 1.5}}>
          {selected.size > 0 ? (
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
              <Button
                variant='contained'
                size='small'
                disabled={submitted}
                onClick={() => {
                  setSubmitted(true);
                  const structured = [...selected].map((key) => {
                    return itemLookup[key] || {type: 'unknown', label: key, submitLabel: key};
                  });
                  onSubmit(structured);
                }}
                sx={{bgcolor: '#056906', '&:hover': {bgcolor: '#044d05'}, textTransform: 'none', fontWeight: 600}}
              >
                {submitted
                  ? 'Submitted'
                  : isDefinition
                  ? `Define Group (${selected.size} selected)`
                  : `Submit Selection (${selected.size})`}
              </Button>
              {!submitted && (
                <Button
                  size='small'
                  onClick={() => {
                    setSelected(new Set());
                  }}
                  sx={{textTransform: 'none', color: '#999', fontSize: '0.75rem'}}
                >
                  Clear selection
                </Button>
              )}
            </Box>
          ) : (
            !submitted &&
            !textDefinitionMode && (
              <Typography variant='caption' sx={{color: '#888', fontStyle: 'italic', display: 'block'}}>
                Select from the categories above to continue
              </Typography>
            )
          )}
        </Box>
      </Box>
      {!submitted && (
        <Box sx={{mt: 2}}>
          <Divider
            sx={{
              my: 1,
              color: '#999',
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.15em',
              '&::before, &::after': {borderColor: '#e0e0e0'},
            }}
          >
            OR
          </Divider>
          {textDefinitionMode ? (
            <Box
              sx={{
                p: 1.5,
                bgcolor: '#f0f9f4',
                border: '2px solid #2e7d32',
                borderRadius: 1.5,
              }}
            >
              <Typography variant='caption' sx={{color: '#2e7d32', fontWeight: 600, display: 'block', mb: 1}}>
                Describe your group
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                autoFocus
                size='small'
                placeholder='e.g. "all vehicles with JD in the name" or "Lodi vehicle label"'
                value={typedText}
                onChange={(e) => {
                  return setTypedText(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && typedText.trim()) {
                    e.preventDefault();
                    setSubmitted(true);
                    onSubmit([typedText.trim()]);
                  }
                }}
                sx={{
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {borderColor: '#ccc'},
                    '&:hover fieldset': {borderColor: '#2e7d32'},
                    '&.Mui-focused fieldset': {borderColor: '#2e7d32'},
                  },
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 1,
                  gap: 1,
                }}
              >
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => {
                    gdDispatch(setTextDefinitionMode(false));
                  }}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#1976d2',
                    borderWidth: 1.5,
                    borderRadius: 1.5,
                    color: '#1976d2',
                    fontWeight: 600,
                    px: 1.75,
                    py: 0.5,
                    '&:hover': {
                      borderColor: '#0d47a1',
                      borderWidth: 1.5,
                      bgcolor: 'rgba(25,118,210,0.05)',
                    },
                  }}
                >
                  ← Back to options
                </Button>
                <Button
                  variant='contained'
                  size='small'
                  disabled={!typedText.trim()}
                  onClick={() => {
                    setSubmitted(true);
                    onSubmit([typedText.trim()]);
                  }}
                  sx={{bgcolor: '#056906', '&:hover': {bgcolor: '#044d05'}, textTransform: 'none', fontWeight: 600}}
                >
                  Submit Definition
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<ChatBubbleOutlineIcon sx={{fontSize: 16}} />}
                onClick={() => {
                  gdDispatch(setTextDefinitionMode(true));
                }}
                sx={{
                  textTransform: 'none',
                  borderColor: '#1976d2',
                  borderWidth: 1.5,
                  borderRadius: 1.5,
                  color: '#1976d2',
                  fontWeight: 600,
                  px: 2.25,
                  py: 0.75,
                  '&:hover': {
                    borderColor: '#0d47a1',
                    borderWidth: 1.5,
                    bgcolor: 'rgba(25,118,210,0.05)',
                  },
                }}
              >
                Describe instead
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/** @return {JSX.Element} */
function GroupedEntitySelectOptions({options, onSubmit}) {
  return <GroupSelectOptions options={options} onSubmit={onSubmit} mode='entitySelect' />;
}

/** @return {JSX.Element} */
function GroupDefinitionOptions({options, onSubmit}) {
  return <GroupSelectOptions options={options} onSubmit={onSubmit} mode='definition' />;
}

/**
 * Single-click option pills for approach/procedural disambiguation.
 * User can click an option OR type a custom response in the chat input.
 * @param {Object} props
 * @param {Array} props.options - Array of option objects or strings.
 * @param {Function} props.onSubmit - Callback with selected label array.
 * @return {JSX.Element}
 */
function ChoiceSelectOptions({options, onSubmit, prompt}) {
  return (
    <Box sx={{mt: 2, width: '100%', minWidth: 0}}>
      {prompt && (
        <Typography variant='caption' sx={{color: '#666', fontStyle: 'italic', display: 'block', mb: 1}}>
          {prompt}
        </Typography>
      )}
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%', minWidth: 0}}>
        {options.map((opt) => {
          const label = typeof opt === 'string' ? opt : opt.label;
          const submitValue = typeof opt === 'object' && opt.submitLabel ? opt.submitLabel : label;
          return (
            <Button
              key={label}
              variant='outlined'
              size='small'
              onClick={() => {
                onSubmit([submitValue]);
              }}
              sx={{
                textTransform: 'none',
                borderColor: '#056906',
                color: '#056906',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                textAlign: 'center',
                maxWidth: '100%',
                minWidth: 0,
                flex: {xs: '1 1 100%', sm: '0 1 auto'},
                fontSize: {xs: '0.75rem', sm: '0.8125rem'},
                py: {xs: 0.75, sm: 0.5},
                px: {xs: 1.25, sm: 1.5},
                '&:hover': {bgcolor: 'rgba(5, 105, 6, 0.08)', borderColor: '#044d05'},
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

const InsightsChatMessage = React.memo(function InsightsChatMessage({
  msg,
  index,
  layout = 'fullPage',
  threadId = null,
  onClarificationSubmit = null,
  onAbortGroupDefinition = null,
}) {
  const dispatch = useDispatch();
  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });
  const iframeRef = useRef(null);
  const textExportRef = useRef(null);
  const captionExportRef = useRef(null);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState(msg.feedback?.comments || '');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [pendingRanking, setPendingRanking] = useState(null);
  const [saveReportOpen, setSaveReportOpen] = useState(false);

  const isUser = msg.role === 'user';
  const isAssistant = msg.role === 'assistant';
  const hasHtml = msg.html && msg.html.length > 0;
  const hasCaptionBelowHtml = hasHtml && msg.content && msg.content !== 'Generated visualization';
  const cfg = LAYOUT_CONFIG[layout] || LAYOUT_CONFIG.fullPage;
  const hasExportableText = Boolean(msg.content && String(msg.content).trim().length > 0);
  const hasDataDownload = Boolean(msg.download?.datasets?.length);
  const hasReportableData = Boolean(msg.dataQuery?.length);
  const showExport = isAssistant && msg.statusUpdate !== true && (hasHtml || hasExportableText || hasDataDownload);
  const isClarificationMessage =
    msg.mode === 'clarification' && Array.isArray(msg.options) && msg.options.length > 0 && onClarificationSubmit;
  const textBubbleWidth =
    isClarificationMessage && layout === 'panel'
      ? '100%'
      : isClarificationMessage
      ? {xs: '100%', sm: cfg.textMaxWidth}
      : cfg.textMaxWidth;

  useEffect(() => {
    setIframeReady(false);
  }, [msg.html]);

  // Reset feedback state when message identity changes (thread switch or new data)
  useEffect(() => {
    setFeedbackComment(msg.feedback?.comments || '');
    setFeedbackOpen(false);
    setPendingRanking(null);
  }, [msg.turnId, msg.feedback?.ranking, msg.feedback?.comments]);

  const buildBaseName = useCallback(() => {
    return `insights-export-${index}-${Date.now()}`;
  }, [index]);

  const runExportPng = useCallback(async () => {
    const base = buildBaseName();
    if (hasHtml) {
      const iframeEl = iframeRef.current;
      if (!iframeEl || !iframeReady) {
        throw new Error('Visualization is still loading. Try again in a moment.');
      }
      const vizCanvas = await captureIframeDocumentToCanvas(iframeEl);
      if (hasCaptionBelowHtml && captionExportRef.current) {
        const capCanvas = await captureElementToCanvas(captionExportRef.current);
        downloadPngFromCanvas(mergeCanvasesVertical(vizCanvas, capCanvas), `${base}.png`);
      } else {
        downloadPngFromCanvas(vizCanvas, `${base}.png`);
      }
      return;
    }
    const el = textExportRef.current;
    if (!el) {
      throw new Error('Nothing to export');
    }
    const canvas = await captureElementToCanvas(el);
    downloadPngFromCanvas(canvas, `${base}.png`);
  }, [hasHtml, hasCaptionBelowHtml, iframeReady, buildBaseName]);

  const runExportPdf = useCallback(async () => {
    const base = buildBaseName();
    if (hasHtml) {
      const iframeEl = iframeRef.current;
      if (!iframeEl || !iframeReady) {
        throw new Error('Visualization is still loading. Try again in a moment.');
      }
      const vizCanvas = await captureIframeDocumentToCanvas(iframeEl);
      if (hasCaptionBelowHtml && captionExportRef.current) {
        const capCanvas = await captureElementToCanvas(captionExportRef.current);
        downloadPdfFromCanvases([mergeCanvasesVertical(vizCanvas, capCanvas)], `${base}.pdf`);
      } else {
        downloadPdfFromCanvases([vizCanvas], `${base}.pdf`);
      }
      return;
    }
    const el = textExportRef.current;
    if (!el) {
      throw new Error('Nothing to export');
    }
    const canvas = await captureElementToCanvas(el);
    downloadPdfFromCanvases([canvas], `${base}.pdf`);
  }, [hasHtml, hasCaptionBelowHtml, iframeReady, buildBaseName]);

  function recordDownload(format) {
    if (!threadId || !msg.turnId) return;
    fetchPostAuthSafe(
      `/insights/thread/${encodeURIComponent(threadId)}/feedback`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include',
        body: JSON.stringify({turnId: msg.turnId, download: true, format}),
      },
      userSettings?.username || '',
      userSettings?.databaseName || ''
    ).catch((e) => {
      console.error('[feedback] recordDownload error:', e);
    });
  }

  const handleExportPng = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      await runExportPng();
      recordDownload('png');
    } catch (e) {
      setExportError(e?.message || 'PNG export failed');
    } finally {
      setExporting(false);
    }
  }, [runExportPng]);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      await runExportPdf();
      recordDownload('pdf');
    } catch (e) {
      setExportError(e?.message || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  }, [runExportPdf]);

  function handleIframeLoaded() {
    setIframeReady(true);
  }

  const currentRanking = msg.feedback?.ranking || null;
  const showFeedback = isAssistant && msg.statusUpdate !== true && (msg.content || msg.html);

  async function submitFeedback(ranking) {
    if (!ranking) {
      console.warn('submitFeedback: no ranking provided');
      return;
    }
    if (!threadId) {
      console.warn('submitFeedback: no threadId available');
      return;
    }
    if (!msg.turnId) {
      console.warn('submitFeedback: no turnId on message', index);
      return;
    }
    setFeedbackSubmitting(true);
    try {
      const response = await fetchPostAuthSafe(
        `/insights/thread/${encodeURIComponent(threadId)}/feedback`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          credentials: 'include',
          body: JSON.stringify({
            turnId: msg.turnId,
            ranking,
            comments: feedbackComment.trim() || null,
          }),
        },
        userSettings?.username || '',
        userSettings?.databaseName || ''
      );
      const responseData = await response.json();
      console.log('[feedback] response:', response.status, responseData);
      if (response.ok) {
        console.log('[feedback] dispatching setMessageFeedback index:', index, 'feedback:', responseData.feedback);
        dispatch(setMessageFeedback({index, feedback: responseData.feedback}));
        setFeedbackOpen(false);
      } else {
        console.error('Feedback submission failed:', response.status, responseData);
      }
    } catch (e) {
      console.error('Failed to submit feedback:', e);
    } finally {
      setFeedbackSubmitting(false);
    }
  }

  function handleThumbClick(ranking) {
    console.log('[feedback] thumb clicked:', ranking, 'turnId:', msg.turnId, 'threadId:', threadId, 'msg:', msg);
    if (currentRanking === ranking) {
      setFeedbackOpen(!feedbackOpen);
      return;
    }
    setPendingRanking(ranking);
    setFeedbackOpen(true);
    submitFeedback(ranking).catch((e) => {
      console.error('[feedback] submitFeedback error:', e);
    });
  }

  function handleCommentSubmit() {
    const ranking = pendingRanking || currentRanking;
    console.log('[feedback] comment submit:', ranking, 'comment:', feedbackComment, 'turnId:', msg.turnId);
    if (!ranking) {
      console.warn('[feedback] no ranking selected');
      return;
    }
    submitFeedback(ranking).catch((e) => {
      console.error('[feedback] submitFeedback error:', e);
    });
  }

  function handleCloseExportError() {
    setExportError(null);
  }

  function handleOpenExportMenu(event) {
    setExportMenuAnchor(event.currentTarget);
  }

  function handleCloseExportMenu() {
    setExportMenuAnchor(null);
  }

  async function handleMenuExportPng() {
    handleCloseExportMenu();
    await handleExportPng();
  }

  async function handleMenuExportPdf() {
    handleCloseExportMenu();
    await handleExportPdf();
  }

  // Raw SQL data rows (CSV) delivered on the chat stream before the answer.
  // One file per sql-route dataset; targets the underlying data rows, not the
  // generated answer/visualization.
  async function handleMenuExportCsv() {
    handleCloseExportMenu();
    const datasets = msg.download?.datasets;
    if (!Array.isArray(datasets) || datasets.length === 0) {
      return;
    }
    try {
      for (const [i, dataset] of datasets.entries()) {
        if (!dataset?.csv) continue;
        await downloadFile(dataset.csv, dataset.filename || `dataset-${i + 1}.csv`, 'text/csv;charset=utf-8');
      }
      recordDownload('csv');
    } catch (e) {
      setExportError(e?.message || 'CSV export failed');
    }
  }

  if (msg.statusUpdate === true) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'flex-start', mb: 2}}>
        <Paper elevation={1} sx={{p: 1.5, backgroundColor: '#ffffff'}}>
          <CircularProgress size={16} sx={{mr: 1}} />
          <Typography variant='body2' component='span'>
            {msg.status}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            p: hasHtml ? 0 : 1.5,
            maxWidth: hasHtml ? cfg.htmlMaxWidth : textBubbleWidth,
            width: hasHtml || isClarificationMessage ? (hasHtml ? cfg.htmlMaxWidth : textBubbleWidth) : 'auto',
            minWidth: 0,
            boxSizing: 'border-box',
            backgroundColor: isUser ? '#1976d2' : '#ffffff',
            color: isUser ? '#ffffff' : '#000000',
            overflow: 'hidden',
            position: 'relative',
            '&:hover .download-btn': {
              opacity: 1,
            },
          }}
        >
          {showExport && (
            <>
              <Box
                className='download-btn'
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  borderRadius: 1,
                  boxShadow: 1,
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                {hasReportableData && (
                  <Tooltip title='Save as Custom Report'>
                    <span>
                      <IconButton
                        size='small'
                        onClick={() => {
                          setSaveReportOpen(true);
                        }}
                        aria-label='Save as custom report'
                        sx={{p: 0.5}}
                      >
                        <BookmarkAddOutlinedIcon sx={{fontSize: 16}} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title='Download'>
                  <span>
                    <IconButton
                      size='small'
                      onClick={handleOpenExportMenu}
                      disabled={exporting || (hasHtml && !iframeReady)}
                      aria-label='Download message'
                      sx={{p: 0.5}}
                    >
                      {exporting ? <CircularProgress size={14} /> : <DownloadIcon sx={{fontSize: 16}} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={handleCloseExportMenu}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
              >
                <MenuItem onClick={handleMenuExportPng}>
                  <ListItemIcon>
                    <ImageIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Download PNG</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleMenuExportPdf}>
                  <ListItemIcon>
                    <PictureAsPdfIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Download PDF</ListItemText>
                </MenuItem>
                {hasDataDownload && (
                  <MenuItem onClick={handleMenuExportCsv}>
                    <ListItemIcon>
                      <TableChartIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>Download data (CSV)</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          {isClarificationMessage && (
            <Box sx={{pt: 0.5, width: '100%', minWidth: 0}}>
              <MarkdownRenderer
                content={msg.content}
                variant='body2'
                sx={{wordBreak: 'break-word', color: '#000000'}}
              />
              {msg.clarificationType === 'groupDefinition' ? (
                <GroupDefinitionOptions options={msg.options} onSubmit={onClarificationSubmit} />
              ) : msg.clarificationType === 'groupedEntitySelect' ? (
                <GroupedEntitySelectOptions options={msg.options} onSubmit={onClarificationSubmit} />
              ) : msg.clarificationType === 'entitySelect' ? (
                <EntitySelectOptions options={msg.options} onSubmit={onClarificationSubmit} />
              ) : (
                <ChoiceSelectOptions
                  options={msg.options}
                  onSubmit={onClarificationSubmit}
                  prompt={msg.clarificationQuestion}
                />
              )}
            </Box>
          )}

          {msg.content &&
            !hasHtml &&
            !(msg.mode === 'clarification' && Array.isArray(msg.options) && msg.options.length > 0) && (
              <Box ref={textExportRef} sx={{pt: showExport ? 0.5 : 0}}>
                <MarkdownRenderer
                  content={msg.content}
                  variant='body2'
                  sx={{
                    wordBreak: 'break-word',
                    color: isUser ? '#ffffff' : '#000000',
                    pr: showExport ? 6 : 0,
                    ...(isUser && {
                      paddingRight: 0,
                      '& p': {paddingLeft: 0},
                      '& ul, & ol': {paddingLeft: '1.25em', paddingRight: 0},
                    }),
                  }}
                />
              </Box>
            )}

          {hasHtml && (
            <Box sx={{width: '100%'}}>
              <iframe
                ref={iframeRef}
                title={`visualization-${index}`}
                srcDoc={msg.html}
                style={cfg.iframeStyle}
                sandbox={SANDBOX}
                onLoad={handleIframeLoaded}
              />
              {hasCaptionBelowHtml && (
                <Box ref={captionExportRef} sx={{p: 1.5, borderBottom: '1px solid #e0e0e0'}}>
                  <MarkdownRenderer
                    content={msg.content}
                    variant='body2'
                    sx={{
                      wordBreak: 'break-word',
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {isAssistant && msg.statusUpdate !== true && !isClarificationMessage && msg.analysisContext && (
            <Box sx={{px: hasHtml ? 1.5 : 0}}>
              <AnalysisDetailsSection analysisContext={msg.analysisContext} />
            </Box>
          )}

          {Array.isArray(msg.appliedGroups) && msg.appliedGroups.length > 0 && (
            <Box sx={{mt: 1, px: hasHtml ? 1.5 : 0}}>
              {msg.appliedGroups.map((group) => {
                const members = group.members || {};
                const dimensionLabels = {
                  vehicle: 'Vehicles',
                  implement: 'Implements',
                  operator: 'Operators',
                  task: 'Tasks',
                  region: 'Regions',
                  field: 'Fields',
                  block: 'Blocks',
                  area: 'Other Areas',
                  timeOfDay: 'Time of Day',
                  dayOfWeek: 'Day of Week',
                  // Legacy keys
                  vehicles: 'Vehicles',
                  implements: 'Implements',
                  operators: 'Operators',
                  tasks: 'Tasks',
                  locations: 'Locations',
                };
                const dims = Object.entries(members).filter(([, v]) => {
                  return Array.isArray(v) && v.length > 0;
                });
                const tooltipContent = (
                  <span>
                    {dims.map(([dim, v], i) => {
                      return (
                        <span key={dim}>
                          <strong>{dimensionLabels[dim] || dim}:</strong>
                          <br />
                          {v.join(', ')}
                          {i < dims.length - 1 && (
                            <>
                              <br />
                              <br />
                            </>
                          )}
                        </span>
                      );
                    })}
                  </span>
                );
                return (
                  <Box key={group.name} sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                    <Typography variant='caption' sx={{color: '#666', fontStyle: 'italic'}}>
                      Reporting on custom group &lsquo;{group.name}&rsquo;
                    </Typography>
                    <Tooltip title={tooltipContent} arrow placement='top'>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          border: '1px solid #bbb',
                          cursor: 'help',
                          fontSize: 10,
                          color: '#888',
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        ?
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 0.5,
              px: hasHtml ? 1.5 : 0,
              pb: hasHtml ? 1 : 0,
            }}
          >
            <Typography
              variant='caption'
              sx={{
                opacity: 0.7,
                fontSize: '0.7rem',
              }}
            >
              {new Date(msg.timestamp || msg.createdAt).toLocaleTimeString()}
            </Typography>

            {showFeedback && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25, ml: 1}}>
                <Tooltip title='Helpful'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      handleThumbClick('up');
                    }}
                    disabled={feedbackSubmitting}
                    sx={{p: 0.25}}
                  >
                    {currentRanking === 'up' ? (
                      <ThumbUpIcon sx={{fontSize: 14, color: '#4caf50'}} />
                    ) : (
                      <ThumbUpOutlinedIcon sx={{fontSize: 14, opacity: 0.5}} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title='Not helpful'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      handleThumbClick('down');
                    }}
                    disabled={feedbackSubmitting}
                    sx={{p: 0.25}}
                  >
                    {currentRanking === 'down' ? (
                      <ThumbDownIcon sx={{fontSize: 14, color: '#f44336'}} />
                    ) : (
                      <ThumbDownOutlinedIcon sx={{fontSize: 14, opacity: 0.5}} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {showFeedback && (
            <Collapse in={feedbackOpen}>
              <Box sx={{px: hasHtml ? 1.5 : 0, pb: 1, pt: 0.5}}>
                <TextField
                  size='small'
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  placeholder='Add a comment (optional)'
                  value={feedbackComment}
                  onChange={(e) => {
                    setFeedbackComment(e.target.value);
                  }}
                  sx={{mb: 0.5, '& .MuiInputBase-input': {fontSize: '0.8rem'}}}
                />
                <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'flex-end'}}>
                  <Button
                    size='small'
                    onClick={() => {
                      setFeedbackOpen(false);
                    }}
                    sx={{fontSize: '0.7rem', textTransform: 'none'}}
                  >
                    Close
                  </Button>
                  <Button
                    size='small'
                    variant='contained'
                    disabled={feedbackSubmitting}
                    onClick={handleCommentSubmit}
                    sx={{fontSize: '0.7rem', textTransform: 'none'}}
                  >
                    {feedbackComment.trim() ? 'Submit Comment' : 'Submit'}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={Boolean(exportError)}
        autoHideDuration={6000}
        onClose={handleCloseExportError}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert severity='error' onClose={handleCloseExportError} sx={{width: '100%'}}>
          {exportError}
        </Alert>
      </Snackbar>

      {hasReportableData && (
        <SaveAsReportModal
          open={saveReportOpen}
          onClose={() => {
            setSaveReportOpen(false);
          }}
          threadId={threadId}
          msg={msg}
        />
      )}
    </>
  );
});

/**
 * @deprecated Prefer <InsightsChatMessage /> in JSX. Kept for minimal call-site churn.
 * @param {Object} msg Chat turn.
 * @param {number} index Message index.
 * @param {'fullPage'|'panel'} [layout='fullPage'] Layout variant.
 * @param {string|null} [threadId=null] Thread identifier for feedback submission.
 * @return {JSX.Element} Wrapped message.
 */
function renderInsightsChatMessage(msg, index, layout = 'fullPage', threadId = null) {
  return <InsightsChatMessage msg={msg} index={index} layout={layout} threadId={threadId} />;
}

export {InsightsChatMessage, renderInsightsChatMessage};
